import {
  type ChangeEvent,
  type MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import { Can } from '@gbs-monorepo-packages/auth';
import {
  BaseTooltip,
  DefaultDescriptionBenefitEducation,
  HighUserRoles,
  formatUnknownErrorMessage,
  generateNegativeTimestamp,
  useToast,
} from '@gbs-monorepo-packages/common';

import { type IUpdateGlobalStyle } from '../../../../constants/GlobalStyles';
import { COURSES, COURSES_TEMPLATE } from '../../../../constants/RoutePaths';
import {
  type ICoursePage,
  type ITemplatePage,
} from '../../../../contexts/coursePage';
import { useCourse } from '../../../../hooks/useCourse';
import { useCoursePages } from '../../../../hooks/useCoursePages';
import { type ICoursePageDTO } from '../../../../services/coursePages';
import {
  type ICourseDTO,
  type ICourseSettingsProps,
  type IFontDTO,
  type IFontsList,
  type IGlobalStyleDTO,
  saveCourseFonts,
  updateGlobalStyleCourse,
} from '../../../../services/courses';
import { type ITemplatePageDTO } from '../../../../services/templates';
import { type ITranslationSettings } from '../../../../services/translation';
import { getRouteFrom } from '../../../../utils/getRoutes';
import { FontsModal } from '../FontsModal';
import { GlobalStyleModal } from '../GlobalStyleModal';
import { SavePageAsTemplateModal } from '../SavePageAsTemplateModal';
import { SettingCourseModal } from '../SettingCourseModal';
import { WarningGlobalStyleModal } from '../WarningGlobalStyleModal';
import {
  ActionCourseButton,
  ActionCourseContainer,
  ActionLabel,
  BackIcon,
  BackPageButton,
  ContentHeaderContainer,
  ExtraCourseButton,
  ExtraCourseContainer,
  FontsIcon,
  Header,
  LastUpdateText,
  SettingsIcon,
  StyleIcon,
  TemplateIcon,
  TitleContainer,
  TitlePage,
} from './styles';

export interface IUpdatedSettingCourseHeader {
  indexSelected: number;
  coursePages: ICoursePage[];
}

export interface ISettingCourseHeaderProps {
  dataCy?: string;
  isAllowedToEdit?: boolean;
  isUserAdmin?: boolean;
  isUserManager?: boolean;
  isClientFullCourseEditor?: boolean;
  isDropdownOpen?: boolean;
  lastCoursePages: MutableRefObject<ICoursePageDTO[]>;
  lastTemplatePage: MutableRefObject<ITemplatePageDTO[]>;
  isCreateCourse?: boolean;
  loading?: boolean;
  needSave?: boolean;
  onChangeTitle?: (newTitle: string) => void;
  onCourseFontsChange?: (newFonts: IFontDTO[]) => void;
  onDiscardCourse?: (newCoursePages: ICoursePage[]) => void;
  onDiscardTemplate?: (newTemplatePages: ITemplatePage[]) => void;
  onSaveModifications?: () => Promise<void>;
  onSavePageTemplate?: (
    title: string,
    templateOptions: number
  ) => Promise<void>;
  onSettingsChange?: (newCourse: ICourseSettingsProps) => void;
  onSaveGlobalStyle?: () => void;
  onUpdatePageTemplate?: () => Promise<void>;
  onBackButtonAdditionalAction?: () => void;
}

const Z_INDEX_MODALS = 10;

export const SettingCourseHeader = ({
  dataCy = 'page-header',
  isAllowedToEdit = true,
  isDropdownOpen = false,
  isUserAdmin = true,
  isUserManager = true,
  isClientFullCourseEditor = false,
  isCreateCourse = true,
  lastCoursePages,
  lastTemplatePage,
  loading = false,
  needSave = false,
  onChangeTitle,
  onCourseFontsChange,
  onDiscardCourse,
  onDiscardTemplate,
  onSaveModifications,
  onSavePageTemplate,
  onUpdatePageTemplate,
  onSettingsChange,
  onSaveGlobalStyle,
  onBackButtonAdditionalAction,
}: ISettingCourseHeaderProps): JSX.Element => {
  const { addToast } = useToast();

  const navigate = useNavigate();
  const { clientId = '', courseId = '' } = useParams();

  const [isFontsModalOpen, setIsFontsModalOpen] = useState(false);
  const [isModalGlobalStyleOpen, setIsModalGlobalStyleOpen] = useState(false);
  const [isWarningGlobalStyleOpen, setIsWarningGlobalStyleOpen] =
    useState(false);
  const [isModalSavePageAsTemplateOpen, setIsModalSavePageAsTemplateOpen] =
    useState(false);
  const [loadingSavePageAsTemplate, setLoadingSavePageAsTemplate] =
    useState(false);
  const [loadingSaveGlobalStyle, setLoadingSaveGlobalStyle] = useState(false);

  const [isModalCourseSettingsOpen, setIsModalCourseSettingsOpen] =
    useState(false);

  const [loadingWarningGlobalStyle, setLoadingWarningGlobalStyle] =
    useState(false);

  const [showLastUpdate, setShowLastUpdate] = useState(false);

  const isCourseEnableToEdit =
    isClientFullCourseEditor || isUserAdmin || isUserManager;

  const [loadingSave, setLoadingSave] = useState(false);
  const hasMountedRef = useRef(false);

  const {
    selectedCourse,
    courseSettings,
    updateCourse,
    updateCourseSettings,
    selectedTemplate,
    updateTemplate,
    updateTranslationSettings,
  } = useCourse();

  const { coursePages, overwriteCoursePages, overwriteTemplatePages } =
    useCoursePages();

  const handleGoBack = useCallback(() => {
    if (onBackButtonAdditionalAction) {
      onBackButtonAdditionalAction();
    }

    if (isCreateCourse) {
      navigate(generatePath(getRouteFrom(COURSES), { clientId }), {
        replace: true,
      });
    } else {
      navigate(generatePath(getRouteFrom(COURSES_TEMPLATE), { clientId }), {
        replace: true,
      });
    }
  }, [clientId, navigate, isCreateCourse, onBackButtonAdditionalAction]);

  const handleChangeTitle = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const newTitle = target.value.slice(0, 90);

    if (isCreateCourse && selectedCourse) {
      updateCourse({
        ...selectedCourse,
        title: newTitle,
      });

      onChangeTitle?.(newTitle);
    }

    if (!isCreateCourse && selectedTemplate) {
      updateTemplate({
        ...selectedTemplate,
        title: newTitle,
      });

      onChangeTitle?.(newTitle);
    }
  };

  const handleFocus = () => {
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false);
  };

  const lastUpdateTitle = useMemo(() => {
    const updatedAt = isCreateCourse
      ? selectedCourse?.updatedAt
      : selectedTemplate?.updatedAt;
    if (!updatedAt) return '';
    const d = new Date(updatedAt);
    if (Number.isNaN(d.getTime())) return '';
    return `Last updated at ${d.toLocaleString()}`;
  }, [isCreateCourse, selectedCourse?.updatedAt, selectedTemplate?.updatedAt]);

  useEffect(() => {
    if (!lastUpdateTitle) return;
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    setShowLastUpdate(true);
    const timeout = setTimeout(() => setShowLastUpdate(false), 3000);
    return () => clearTimeout(timeout);
  }, [lastUpdateTitle]);

  const handleOpenFontsModal = () => {
    setIsFontsModalOpen(true);
  };

  const handleOpenGlobalStyleModal = useCallback(() => {
    if (needSave) {
      setIsWarningGlobalStyleOpen(true);
      return;
    }

    setIsModalGlobalStyleOpen(true);
  }, [needSave]);

  const handleAddFontsToCourse = async (fontsList: IFontsList) => {
    try {
      const course = await saveCourseFonts({
        courseId: Number(courseId),
        fonts: fontsList.fonts,
      });

      addToast({
        title: 'Course fonts updated!',
        styleType: 'success',
        dataCy: 'success-toast-title',
      });

      onCourseFontsChange?.(course.fonts);
      updateCourse(course);
      setIsFontsModalOpen(false);
    } catch (err) {
      addToast({
        title: 'Error loading custom fonts',
        description: formatUnknownErrorMessage(
          err,
          DefaultDescriptionBenefitEducation
        ),
        styleType: 'error',
        dataCy: 'error-toast',
        duration: 3000,
      });
    }
  };

  const handleDeclineAddFonts = () => {
    setIsFontsModalOpen(false);
  };

  const handleDeclineGlobalStyle = () => {
    setIsModalGlobalStyleOpen(false);
  };

  const handleDeclineWarningGlobalStyle = () => {
    setIsWarningGlobalStyleOpen(false);
  };

  const handleOpenSavePageAsTemplate = () => {
    setIsModalSavePageAsTemplateOpen(true);
  };

  const handleSavePageTemplate = useCallback(
    async (title: string, templateOptions: number) => {
      setLoadingSavePageAsTemplate(true);

      try {
        await onSavePageTemplate?.(title, templateOptions);
        setIsModalSavePageAsTemplateOpen(false);
      } catch (err) {
        addToast({
          title: 'Error saving global styles',
          description: formatUnknownErrorMessage(
            err,
            DefaultDescriptionBenefitEducation
          ),
          styleType: 'error',
          dataCy: 'error-toast',
          duration: 3000,
        });
      }

      setLoadingSavePageAsTemplate(false);
    },
    [onSavePageTemplate, addToast]
  );

  const handleSaveGlobalStyle = useCallback(
    async (data: IUpdateGlobalStyle) => {
      setLoadingSaveGlobalStyle(true);
      if (!selectedCourse?.globalStyle) {
        setLoadingSaveGlobalStyle(false);
        addToast({
          title: 'Error on save global style',
          description: 'An error occurred while updating the global style',
          styleType: 'error',
          dataCy: 'error-toast',
        });
        return;
      }
      await updateGlobalStyleCourse({
        id: selectedCourse.globalStyle.id,
        ...data,
      })
        .then((response: IGlobalStyleDTO) => {
          const updated: ICourseDTO = {
            ...selectedCourse,
            globalStyle: response,
          };

          updateCourse(updated);

          addToast({
            title: 'Success',
            description: 'Global style has been updated!',
            styleType: 'success',
            dataCy: 'success-toast',
            duration: 3000,
          });
          onSaveGlobalStyle?.();

          setLoadingSaveGlobalStyle(false);
          setIsModalGlobalStyleOpen(false);
        })
        .catch((err) => {
          setLoadingSaveGlobalStyle(false);
          addToast({
            title: 'Error on save global styles',
            description: formatUnknownErrorMessage(
              err,
              DefaultDescriptionBenefitEducation
            ),
            styleType: 'error',
            dataCy: 'error-toast',
            duration: 3000,
          });
        });
    },
    [addToast, onSaveGlobalStyle, selectedCourse, updateCourse]
  );

  const handleDeclineSavePageTemplate = useCallback(() => {
    setIsModalSavePageAsTemplateOpen(false);
  }, []);

  const handleOpenCourseSettings = () => {
    setIsModalCourseSettingsOpen(true);
  };

  const handleNewCourseSettings = useCallback(
    (
      passcodeProtected: boolean,
      passcode: string,
      viewURL: string,
      courseStatus: string,
      translationSettings: ITranslationSettings | null
    ) => {
      if (!selectedCourse) {
        return;
      }

      const updatedSettings: ICourseSettingsProps = {
        passcodeProtected:
          passcodeProtected ?? selectedCourse.passcodeProtected,
        passcode: passcode ?? selectedCourse.passcode,
        viewUrl: viewURL ?? selectedCourse?.viewURL,
        status: courseStatus ?? selectedCourse?.status,
      };

      updateCourseSettings(updatedSettings);
      updateTranslationSettings(translationSettings);

      onSettingsChange?.(updatedSettings);
      setIsModalCourseSettingsOpen(false);
    },
    [
      onSettingsChange,
      selectedCourse,
      updateCourseSettings,
      updateTranslationSettings,
    ]
  );

  const handleDeclineNewCourseSettings = useCallback(() => {
    setIsModalCourseSettingsOpen(false);
  }, []);

  const handleDiscardCourse = useCallback(() => {
    const coursePagesIds = new Set(coursePages.map(({ id }) => id));
    let tempId = generateNegativeTimestamp();

    const newCoursePages = overwriteCoursePages(lastCoursePages.current);

    for (const newCoursePage of newCoursePages) {
      if (!coursePagesIds.has(newCoursePage.id)) {
        newCoursePage.isNew = true;
        newCoursePage.id = tempId;
        tempId--;
      }
    }

    onDiscardCourse?.(newCoursePages);
  }, [coursePages, lastCoursePages, onDiscardCourse, overwriteCoursePages]);

  const handleDiscardTemplate = useCallback(() => {
    const newTemplatePages = overwriteTemplatePages(lastTemplatePage.current);
    onDiscardTemplate?.(newTemplatePages);
  }, [lastTemplatePage, onDiscardTemplate, overwriteTemplatePages]);

  const handleSaveModificationsCourse = useCallback(async () => {
    if (!selectedCourse?.title.trim()) {
      addToast({
        title: 'Course title is empty',
        description:
          'Please make sure to provide a title for your course to proceed.',
        styleType: 'error',
        dataCy: 'error-toast-course-title',
      });
      return;
    }
    setLoadingSave(true);
    setIsWarningGlobalStyleOpen(false);

    await onSaveModifications?.();

    setLoadingSave(false);
  }, [onSaveModifications, addToast, selectedCourse]);

  const handleSaveWarningGlobalStyle = useCallback(async () => {
    if (!selectedCourse?.title.trim()) {
      addToast({
        title: 'Course title is empty',
        description:
          'Please make sure to provide a title for your course to proceed.',
        styleType: 'error',
        dataCy: 'error-toast-course-title',
      });
      return;
    }
    setLoadingWarningGlobalStyle(true);

    await onSaveModifications?.().finally(() => {
      setLoadingWarningGlobalStyle(false);
      setIsWarningGlobalStyleOpen(false);
      setIsModalGlobalStyleOpen(true);
    });
  }, [onSaveModifications, addToast, selectedCourse]);

  const handleSaveModificationsTemplate = useCallback(async () => {
    if (!selectedTemplate?.title.trim()) {
      addToast({
        title: 'Template title is empty',
        description:
          'Please make sure to provide a title for your template to proceed.',
        styleType: 'error',
        dataCy: 'error-toast-template-title',
      });
      return;
    }
    setLoadingSave(true);

    await onUpdatePageTemplate?.();

    setLoadingSave(false);
  }, [onUpdatePageTemplate, addToast, selectedTemplate]);

  const titleHeader =
    (isCreateCourse
      ? (selectedCourse?.title ?? 'Course Name')
      : selectedTemplate?.title) ?? '';

  const titleRef = useRef<HTMLInputElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const checkTruncation = useCallback(() => {
    if (titleRef.current) {
      const element = titleRef.current;
      const isTextTruncated = element.scrollWidth > element.clientWidth;
      setIsTruncated(isTextTruncated);
    }
  }, [titleHeader]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      checkTruncation();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [checkTruncation]);

  const actionButtonsHeaderCreateCourse = (
    <>
      <Can roles={HighUserRoles}>
        <ExtraCourseButton
          data-cy="course-global-style-button"
          disabled={loading || !isAllowedToEdit}
          onClick={handleOpenGlobalStyleModal}
        >
          <StyleIcon />
          <ActionLabel data-cy="button-globalStyle">Global Style</ActionLabel>
        </ExtraCourseButton>
      </Can>
      <Can roles={HighUserRoles}>
        <ExtraCourseButton
          data-cy="course-fonts-button"
          disabled={loading || !isAllowedToEdit}
          onClick={handleOpenFontsModal}
        >
          <FontsIcon />
          <ActionLabel data-cy="button-fonts">Fonts</ActionLabel>
        </ExtraCourseButton>
      </Can>
      <Can roles={HighUserRoles}>
        <ExtraCourseButton
          data-cy="page-save-template-button"
          disabled={loading || !isAllowedToEdit}
          onClick={handleOpenSavePageAsTemplate}
        >
          <TemplateIcon />
          <ActionLabel data-cy="button-saveTemplate">Save Template</ActionLabel>
        </ExtraCourseButton>
      </Can>
      <ExtraCourseButton
        data-cy="button-extraCourse"
        disabled={loading || !isAllowedToEdit}
        onClick={handleOpenCourseSettings}
      >
        <SettingsIcon />
        <ActionLabel data-cy="button-settingsCourse">Settings</ActionLabel>
      </ExtraCourseButton>
    </>
  );

  const actionButtonsSaveTemplate = (
    <ActionCourseButton
      disabled={!needSave}
      loading={loadingSave}
      dataCy="button-saveTemplate"
      onClick={() => {
        void handleSaveModificationsTemplate();
      }}
    >
      Save Template
    </ActionCourseButton>
  );

  const actionButtonsUpdateCourse = (
    <ActionCourseButton
      disabled={!needSave}
      loading={loadingSave}
      dataCy="button-saveCourse"
      onClick={() => {
        void handleSaveModificationsCourse();
      }}
    >
      Save Course
    </ActionCourseButton>
  );

  return (
    <Header data-cy={dataCy}>
      <TitleContainer data-cy="course-info-container">
        <BackPageButton data-cy="button-backToCourse" onClick={handleGoBack}>
          <BackIcon />
        </BackPageButton>
        <ContentHeaderContainer>
          <BaseTooltip
            message={titleHeader}
            disabled={!isTruncated || isEditing}
            dataCy="course-title-tooltip"
          >
            <TitlePage
              id="course-title-header"
              dataCy="title-editCourse"
              disabled={loading || (isCreateCourse && !isAllowedToEdit)}
              onChange={handleChangeTitle}
              onFocus={handleFocus}
              onBlur={handleBlur}
              readOnly={!isCourseEnableToEdit}
              ref={titleRef}
            >
              {titleHeader}
            </TitlePage>
          </BaseTooltip>
          {showLastUpdate && (
            <LastUpdateText
              data-cy="course-last-updated"
              role="status"
              aria-live="polite"
            >
              {lastUpdateTitle}
            </LastUpdateText>
          )}
        </ContentHeaderContainer>
      </TitleContainer>
      <ExtraCourseContainer data-cy="course-extra-button-container">
        {isCreateCourse &&
          isCourseEnableToEdit &&
          actionButtonsHeaderCreateCourse}
      </ExtraCourseContainer>
      <ActionCourseContainer data-cy="course-action-button-container">
        {isAllowedToEdit && (
          <>
            <ActionCourseButton
              disabled={!needSave || loadingSave}
              styleType="text"
              dataCy="button-discardCourse"
              onClick={
                isCreateCourse ? handleDiscardCourse : handleDiscardTemplate
              }
            >
              Discard
            </ActionCourseButton>

            {isCreateCourse && actionButtonsUpdateCourse}
            {!isCreateCourse && actionButtonsSaveTemplate}
          </>
        )}
      </ActionCourseContainer>
      {courseId && isFontsModalOpen && (
        <FontsModal
          courseId={Number(courseId)}
          onAccept={handleAddFontsToCourse}
          onDecline={handleDeclineAddFonts}
          open={isFontsModalOpen}
          courseFonts={selectedCourse?.fonts ?? []}
          zIndex={10}
        />
      )}
      <WarningGlobalStyleModal
        loading={loadingWarningGlobalStyle}
        onAccept={handleSaveWarningGlobalStyle}
        onDecline={handleDeclineWarningGlobalStyle}
        open={isWarningGlobalStyleOpen}
        acceptText="Save Course"
        dataCy="save-course-dialogModal"
        mainText={`You need to save the course changes to edit the global style. Do you
      want to save them?`}
        zIndex={Z_INDEX_MODALS}
      />
      {selectedCourse?.globalStyle && (
        <GlobalStyleModal
          courseId={Number(courseId)}
          loading={loadingSaveGlobalStyle}
          onAccept={handleSaveGlobalStyle}
          onDecline={handleDeclineGlobalStyle}
          open={isModalGlobalStyleOpen}
          zIndex={Z_INDEX_MODALS}
          needSave={needSave}
        />
      )}
      <SavePageAsTemplateModal
        loading={loadingSavePageAsTemplate}
        onAccept={handleSavePageTemplate}
        onDecline={handleDeclineSavePageTemplate}
        open={isModalSavePageAsTemplateOpen}
        waitToOpen={isDropdownOpen}
        zIndex={Z_INDEX_MODALS}
        isUserAdmin={isUserAdmin}
        isUserManager={isUserManager}
      />
      <SettingCourseModal
        onAccept={handleNewCourseSettings}
        onDecline={handleDeclineNewCourseSettings}
        courseSettings={courseSettings}
        open={isModalCourseSettingsOpen}
        waitToOpen={isDropdownOpen}
        zIndex={Z_INDEX_MODALS}
      />
    </Header>
  );
};
